import { useRouter } from 'next/router';
import { generateArticleStructuredData } from 'src/lib/utils';

// components
import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import { TemplateWrapper } from 'src/templates';

// helpers
import {
  ArticlePageHeader,
  ArticleTOC,
} from 'src/components/headers/ResourceArticlePageHeader';
import {
  CmsComponents,
  CmsThemes,
  ContentfulTemplates,
  Locales,
  type TLocale,
} from 'src/lib/constants';

import styles from './ArticleTemplate.module.scss';

const ArticleTemplate = ({
  fields,
  locale = Locales.EN_US,
}: {
  fields?;
  locale?: TLocale;
}) => {
  const { asPath } = useRouter();
  const anchors = fields?.components?.filter((component) => {
    return component.sys?.contentType?.sys?.id === CmsComponents.ANCHOR;
  });

  const authors = fields?.authors?.filter((author) => {
    return author?.sys?.type === 'Entry';
  });

  const editors = fields?.editors?.filter((editor) => {
    return editor?.sys?.type === 'Entry';
  });

  return (
    <TemplateWrapper
      meta-title={fields?.metaTitle}
      meta-description={fields?.metaDescription}
      hasFooterCta={false}
      className={styles.resourceArticle}
      openGraphImage={fields?.openGraphImage?.fields?.file?.url}
      structuredData={generateArticleStructuredData({
        authorName: authors?.map((a) => {
          return a.fields.name;
        }),
        editorName: editors?.map((e) => {
          return e.fields.name;
        }),
        datePublished: fields?.published,
        dateModified: fields?.updated || fields?.published,
        headline: fields?.metaTitle,
        image: fields.bannerImage?.fields?.file?.url,
      })}
      cmsTheme={CmsThemes.WEB3}
      locale={locale}
    >
      <div data-testid={ContentfulTemplates.ARTICLE}>
        <ArticlePageHeader
          {...fields}
          title={fields?.metaTitle}
          date={fields?.published}
          updated={fields?.updated}
          authors={authors}
          path={asPath}
        />
        {anchors?.length > 0 && <ArticleTOC anchors={anchors} />}
        <CmsComponentSwitch
          templatePage={ContentfulTemplates.ARTICLE}
          components={fields?.components}
        />
      </div>
      <ContentfulToolbar />
    </TemplateWrapper>
  );
};

export default ArticleTemplate;
